import { Injectable, Inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { HttpHelper } from '@app/common/http-helper';
import { Constants } from '@app/common/constants';
import { Observable, of, EMPTY } from 'rxjs';
import { map, catchError, tap, expand, toArray, delay, concatMap } from 'rxjs/operators';
import { Group } from '@microsoft/microsoft-graph-types';
import { Environment } from '@environments/interface.environment';
import { CUSTOMERENVIRONMENT } from '@app/common/app-tokens';
import { CacheService } from '@app/services/cache.service';
import { CustomEncoder } from '@app/common/custom-http-param-encode';
import { CreateSiteWizardUser } from '@app/models/create-site-wizard-user';
import { User } from '@microsoft/microsoft-graph-types';

@Injectable({
  providedIn: 'root'
})
export class O365GroupService {

  public useCache = true;
  private groupsCachePrefix = 'O365GroupsService-';
  // private getAllGroups$: Observable<Array<Group>>;
  // private getMyGroups$: Observable<Array<Group>>;
  // private getMyOwnedGroups$: Observable<Array<Group>>;

  public flushCache() {
    this.cache.removeAllitemsInPersistentCacheWithPrefix(this.groupsCachePrefix);
  }

  constructor(private http: HttpClient, private cache: CacheService, @Inject(CUSTOMERENVIRONMENT) private environment: Environment) { }


  // Fetch all O365 (Unified) Groups. Pass propertiesToSelect array to select only specific properties, leave empty to select default set of properties
  getAllGroups(propertiesToSelect?: string[]): Observable<Array<Group>> {
      let httpParams = new HttpParams({ encoder: new CustomEncoder() })
        .append('$top', `500`);

      let cacheKeyPrefix = this.groupsCachePrefix + 'getAllGroups';

      if (propertiesToSelect && propertiesToSelect.length > 0) {
        httpParams = httpParams.append('$select', propertiesToSelect.join(','));
        cacheKeyPrefix += '-' + propertiesToSelect.join('-');
      }

      httpParams = httpParams.append('$filter', `groupTypes/any(c:c+eq+'Unified')`);

      const fetchOperation = this.http.get<any>(`${Constants.GRAPH_API_ENDPOINT}${Constants.GRAPH_API_VERSION}/groups`, { params: httpParams })
        .pipe(
          expand((results) => {
            if (results.hasOwnProperty('@odata.nextLink')) {
              return this.http.get<any>(results['@odata.nextLink']);
            } else {
              return EMPTY;
            }
          }),
          toArray(),
          map(results => {
            let allGroups = new Array<Group>();
            results.forEach(result => {
              allGroups = allGroups.concat(result.value as Array<Group>);
            });
            // console.log(`fetched All groups result: `, allGroups);
            return allGroups;
          }),
          tap(response => {
            this.useCache && this.cache.set(cacheKeyPrefix, response as Array<Group>);
          }));

      if (this.useCache) {
        const cachedData = this.cache.get<Array<Group>>(cacheKeyPrefix);
        if (cachedData) {
          return of(cachedData);
        }
      }
      return fetchOperation;

  }

  // Fetch O365 (Unified) Groups that have a displayName starting with search parameter. Pass propertiesToSelect array to select only specific properties, leave empty to select default set of properties
  searchForGroups(find: string, max: number, onlyUnifiedGroups: boolean, propertiesToSelect?: string[]): Observable<Array<Group>> {
      let httpParams = new HttpParams({ encoder: new CustomEncoder() });

    find = find.replace('#', '%23');  // replace # with entity

      let cacheKeyPrefix = this.groupsCachePrefix + `searchForGroups-${find}-${max}`;

      if (propertiesToSelect && propertiesToSelect.length > 0) {
        httpParams = httpParams.append('$select', propertiesToSelect.join(','));
        cacheKeyPrefix += '-' + propertiesToSelect.join('-');
      }

      const groupFilter = onlyUnifiedGroups ? `groupTypes/any(c:c+eq+'Unified') and ` : '';
      httpParams = httpParams.append('$filter', `${groupFilter}startsWith(displayName,'${find}')`);
      if (max) {
        httpParams = httpParams.append('$top', max.toString());
      }

      const fetchOperation = this.http.get<any>(`${Constants.GRAPH_API_ENDPOINT}${Constants.GRAPH_API_VERSION}/groups`, { params: httpParams })
        .pipe(
          toArray(),
          map(results => {
            let groups = new Array<Group>();
            results.forEach(result => {
              groups = groups.concat(result.value as Array<Group>);
            });
            return groups;
          }),
          tap(response => {
            this.useCache && this.cache.set(cacheKeyPrefix, response as Array<Group>);
          }));

      if (this.useCache) {
        const cachedData = this.cache.get<Array<Group>>(cacheKeyPrefix);
        if (cachedData) {
          return of(cachedData);
        }
      }
      return fetchOperation;

  }

  // Fetch O365 (Unified) groups where I am member. Pass propertiesToSelect array to select only specific properties, leave empty to select default set of properties
  getMyO365Groups(propertiesToSelect?: string[]): Observable<Array<Group>> {
      let httpParams = new HttpParams({ encoder: new CustomEncoder() })
        .append('$top', `500`);

      let cacheKeyPrefix = this.groupsCachePrefix + 'getMyO365Groups';


      if (propertiesToSelect && propertiesToSelect.length > 0) {
        propertiesToSelect.push('groupTypes');
        httpParams = httpParams.append('$select', propertiesToSelect.join(','));
        cacheKeyPrefix += '-' + propertiesToSelect.join('-');
      } else {
        httpParams = httpParams.append('$select', 'groupTypes');
      }

      const fetchOperation = this.http.get<any>(`${Constants.GRAPH_API_ENDPOINT}${Constants.GRAPH_API_VERSION}/me/memberOf?`, { params: httpParams })
        .pipe(
          expand((results) => {
            if (results.hasOwnProperty('@odata.nextLink')) {
              return this.http.get<any>(results['@odata.nextLink']);
            } else {
              return EMPTY;
            }
          }),
          toArray(),
          map(results => {
            let allGroups = new Array<Group>();
            results.forEach(result => {
              allGroups = allGroups.concat(result.value as Array<Group>);
            });
            allGroups = allGroups.filter(g => g.groupTypes && g.groupTypes.includes('Unified') );
            // console.log(`fetched MY groups result: `, allGroups);
            return allGroups;
          }),
          tap(response => {
            this.useCache && this.cache.set(cacheKeyPrefix, response as Array<Group>);
          }));

      if (this.useCache) {
        const cachedData = this.cache.get<Array<Group>>(cacheKeyPrefix);
        if (cachedData) {
          return of(cachedData);
        }
      }
      return fetchOperation;

  }



  // Fetch O365 (Unified) groups that I OWN. Pass propertiesToSelect array to select only specific properties, leave empty to select default set of properties
  getMyOwnedGroups(propertiesToSelect?: string[]): Observable<Array<Group>> {

      let httpParams = new HttpParams({ encoder: new CustomEncoder() })
        .append('$top', `500`);

      let cacheKeyPrefix = this.groupsCachePrefix + 'getMyOwnedGroups';


      if (propertiesToSelect && propertiesToSelect.length > 0) {
        propertiesToSelect.push('groupTypes');
        httpParams = httpParams.append('$select', propertiesToSelect.join(','));
        cacheKeyPrefix += '-' + propertiesToSelect.join('-');
      } else {
        httpParams = httpParams.append('$select', 'groupTypes');
      }

      const fetchOperation = this.http.get<any>(`${Constants.GRAPH_API_ENDPOINT}${Constants.GRAPH_API_VERSION}/me/ownedObjects?`, { params: httpParams })
        .pipe(
          expand((results) => {
            if (results.hasOwnProperty('@odata.nextLink')) {
              return this.http.get<any>(results['@odata.nextLink']);
            } else {
              return EMPTY;
            }
          }),
          toArray(),
          map(results => {
            let allGroups = new Array<Group>();
            results.forEach(result => {
              allGroups = allGroups.concat(result.value as Array<Group>);
            });
            allGroups = allGroups.filter(g => g.groupTypes && g.groupTypes.includes('Unified') );
            // console.log(`fetched my OWNED groups result: `, allGroups);
            return allGroups;
          }),
          tap(response => {
            this.useCache && this.cache.set(cacheKeyPrefix, response as Array<Group>);
          }));

      if (this.useCache) {
        const cachedData = this.cache.get<Array<Group>>(cacheKeyPrefix);
        if (cachedData) {
          return of(cachedData);
        }
      }
      return fetchOperation;

  }


  resolveGroupMembers(groupId: string, transitive: boolean): Observable<Array<User>> {
    // Note: groups are not inlcuded, only users
    const httpParams = new HttpParams({ encoder: new CustomEncoder() });
    const endpoint = transitive ? 'transitiveMembers' : 'members';

    const fetchOperation = this.http.get<any>(`${Constants.GRAPH_API_ENDPOINT}${Constants.GRAPH_API_VERSION}/groups/${groupId}/${endpoint}`, { params: httpParams })
      .pipe(
        expand((results) => {
          if (results.hasOwnProperty('@odata.nextLink')) {
            return this.http.get<any>(results['@odata.nextLink']);
          } else {
            return EMPTY;
          }
        }),
        toArray(),
        map(results => {
          const allUsers = new Array<User>();
          results.forEach(result => {
            result.value.forEach(r => {
              if (r.hasOwnProperty('@odata.type')) {
                if (r['@odata.type'].toLowerCase() === '#microsoft.graph.user') {
                  allUsers.push(r as User);
                }
              }
            });
          });
          return allUsers;
        }),
    );

    return fetchOperation;
  }

  addUserAsGroupMember(groupId: string, userId: string): Observable<boolean> {

    const directoryObject = {
      '@odata.id': 'https://graph.microsoft.com/v1.0/directoryObjects/' + userId
    };

    return this.http.post<any>(`${Constants.GRAPH_API_ENDPOINT + Constants.GRAPH_API_VERSION}/groups/${groupId}/members/$ref`, directoryObject)
      .pipe(
        map(() => {
          return true;
        }),
        catchError(HttpHelper.handleError('addUserAsGroupMember', true, false)));
  }

  addMultipleUsersAsGroupMembers(groupId: string, allUserIds: string[]): Observable<boolean> {

    if (allUserIds.length === 0) { return of(true); }

    const httpParams = new HttpParams({ encoder: new CustomEncoder() });
    const addOperation = this.resolveGroupMembers(groupId, false)
      .pipe(
        concatMap(currentMembers => {
          const trimmedUserIds = allUserIds.filter(u => !currentMembers.map(cm => cm.id).includes(u));
          const firstBatch = trimmedUserIds.splice(0, 20);

          return this.executeAddMultipleUsersAsGroupMembers(groupId, firstBatch).pipe(
            expand((result) => {
              const nextBatch = trimmedUserIds.splice(0, 20);
              if (nextBatch.length > 0) {
                return this.executeAddMultipleUsersAsGroupMembers(groupId, nextBatch);
              } else {
                return EMPTY;
              }
            }),
            toArray(),
            map(results => {
              return results.every(r => r === true);
            }));
        })
    );

    return addOperation;
  }

  executeAddMultipleUsersAsGroupMembers(groupId: string, userIds: string[]): Observable<boolean> {

    // userIds must not contains more than 20 ids!

    const directoryObjects = {};
    directoryObjects['members@odata.bind'] = userIds.map(id => {
      return 'https://graph.microsoft.com/v1.0/directoryObjects/' + id;
    });

    return this.http.patch<any>(`${Constants.GRAPH_API_ENDPOINT + Constants.GRAPH_API_VERSION}/groups/${groupId}`, directoryObjects)
      .pipe(
        map(() => {
          return true;
        }),
        catchError(HttpHelper.handleError('addMultipleUsersAsGroupMembers', false, false)));
  }

  isOffice365GroupUrlAvailable(mailNickname: string): Observable<boolean> {
    const createOperation = this.http.get<any>(`${Constants.GRAPH_API_ENDPOINT + Constants.GRAPH_API_VERSION}/groups?$filter=mailNickname eq '${mailNickname}'`)
      .pipe(
        map(response => {
          return (response.value.length !== 1);
        })
      );

    return createOperation;
  }


  getSharePointSiteUrlForGroup(groupId: string): Observable<string> {

    const cacheKey = this.groupsCachePrefix + `getSharePointSiteUrlForGroup-${groupId}`;

    const fetchOperation = this.http.get<any>(`${Constants.GRAPH_API_ENDPOINT + Constants.GRAPH_API_VERSION}/groups/${groupId}/sites/root/weburl`, {})
      .pipe(
        map(response => {
          // console.log(`Fetched site URL for group with ID '${groupId}': `);
          // console.log(response.value);
          return response.value;
        }),
        tap(data => {
          this.useCache && this.cache.set(cacheKey, data);
        }),
        catchError(HttpHelper.handleError('getSharePointSiteUrlForGroup', '', true)));

    if (this.useCache) {
      const cachedData = this.cache.get<string>(cacheKey);
      if (cachedData) {
        return of(cachedData);
      }
    }
    return fetchOperation;
  }

  filterOutUsedGroups(allGroups: Array<Group>): Observable<Array<Group>> {

    const allGroupIds = allGroups.map( g => g.id);

    const fetchOperation = this.http.post<Array<string>>(`${Constants.API_ENDPOINT}/Projects/FilterOutUsedGroups/`, allGroupIds, {
      ...HttpHelper.defaultRequestOptions
    })
      .pipe(
        map(allowedGroups => {
          // console.log(`Groups not already connected to projects:`, allowedGroups);
          const filteredGroups = allGroups.filter(g => allowedGroups.includes(g.id) );
          return filteredGroups;
        }),
        catchError(HttpHelper.handleError('filterOutUsedGroups', new Array<Group>(), true)));

    return fetchOperation;
  }

  createOffice365Group(siteTitle: string, siteUrl: string, privacySetting: string, owners: CreateSiteWizardUser[], members: CreateSiteWizardUser[] ): Observable<Group> {

    // Note: A maximum of 20 relationships, such as owners and members, can be added as part of group creation.

    const group = <Group>{
      displayName: siteTitle,
      mailNickname: siteUrl,
      groupTypes: ['Unified'],
      mailEnabled: true,
      securityEnabled: false,
      visibility: privacySetting
    };

    const ownersIds = owners ? owners.map(user => user.Id) : [];
    const membersIds = members ? members.map(user => user.Id) : [];

    //// add owners as members too
    // ownersIds.forEach(ownerId => {
    //  if (membersIds.indexOf(ownerId) === -1) {
    //    membersIds.push(ownerId);
    //  }
    // });

    /* owners/members format:
        "members@odata.bind": [
            "https://graph.microsoft.com/v1.0/users/24dad241-95a1-48e7-b8ba-a8c8f048e0ef",
            "https://graph.microsoft.com/v1.0/users/a8c8f048e0ef-95a1-48e7-b8ba-24dad241",
        ],
    */

    if (ownersIds.length > 0) {
      group['owners@odata.bind'] = ownersIds.map(ownerId => {
        return `${Constants.GRAPH_API_ENDPOINT + Constants.GRAPH_API_VERSION}/users/${ownerId}`;
      });
    }

    if (membersIds.length > 0) {
      group['members@odata.bind'] = membersIds.map(memberId => {
        return `${Constants.GRAPH_API_ENDPOINT + Constants.GRAPH_API_VERSION}/users/${memberId}`;
      });
    }

    const createOperation = this.http.post<Group>(`${Constants.GRAPH_API_ENDPOINT + Constants.GRAPH_API_VERSION}/groups`, group)
      .pipe(
        delay(10000),    // Artificial delay because otherwise things just flashes by user, and also sites take a while to setup even though adding a group is almost instant
        tap(response => {
          console.log(`Create group responded:`);
          console.log(response);
          this.flushCache();
        },
          error => {
            console.log(`Create group error:`);
            console.log(error);
          }
        ));

    return createOperation;
  }
}
